import {Item} from '@PosterWhiteboard/items/item/item.class';
import QRCode from 'qrcode';
import type {RGBA} from '@Utils/color.util';
import {convertFabricColorToFillObject, rgbaArrayToRgbString} from '@Utils/color.util';
import {rgbaToHexString} from '@Utils/color.util';
import {ElementDataType} from '@Libraries/add-media-library';
import {getPosterUi} from '@PosterWhiteboard/ui/poster-ui';
import type {FabricObject, Group} from '@postermywall/fabricjs-2';
import {loadSVGFromString, util} from '@postermywall/fabricjs-2';
import type {BaseItemObject, CopyableItemStyles, InitItemOpts, ItemObject} from './item/item.types';
import {ITEM_TYPE} from './item/item.types';
import {FillTypes} from '@PosterWhiteboard/classes/fill.class';
import type {DeepPartial} from '@/global';

export interface QRCodeItemObject extends BaseItemObject {
  message: string;
  qrType: QRType;
  qrForegroundColor: RGBA;
  qrBackgroundColor: RGBA;
  isBackgroundTransparent: boolean;
}

/**
 * Narrows a redux `ItemObject` to a `QRCodeItemObject`, so group hooks can read QR-only keys (e.g.
 * `qrForegroundColor`, `message`) without an unchecked cast.
 */
export const isQRCodeItemObject = (item: ItemObject): item is QRCodeItemObject => {
  return item.gitype === ITEM_TYPE.QR_CODE;
};

export const enum QRType {
  LINK = 'qr_link',
  EMAIL = 'qr_email',
  PHONE_NUMBER = 'qr_phone_number'
}

export const DEFAULT_QR_MESSAGE = 'https://www.postermywall.com';
export const DEFAULT_QR_MAX_LENGTH = 1400;

export class QRCodeItem extends Item {
  public message = DEFAULT_QR_MESSAGE;
  public qrType: QRType = QRType.LINK;
  public qrForegroundColor: RGBA = [0, 0, 0, 1];
  public qrBackgroundColor: RGBA = [255, 255, 255, 1];
  public isBackgroundTransparent = true;
  public gitype: ITEM_TYPE.QR_CODE = ITEM_TYPE.QR_CODE;

  protected override itemObjectHasDestructiveChanges = (oldItemObject: QRCodeItemObject): boolean => {
    return (
      oldItemObject.message !== this.message ||
      oldItemObject.qrForegroundColor !== this.qrForegroundColor ||
      oldItemObject.isBackgroundTransparent !== this.isBackgroundTransparent ||
      oldItemObject.qrBackgroundColor !== this.qrBackgroundColor
    );
  };

  public override getCopyableStyles(): CopyableItemStyles {
    return {
      ...super.getCopyableStyles(),
      frontFill: rgbaArrayToRgbString(this.qrForegroundColor),
      backFill: {
        fillType: this.isBackgroundTransparent ? FillTypes.NONE : FillTypes.SOLID,
        fillColor: [this.qrBackgroundColor],
      },
    };
  }

  public override async pasteStyles(copiedStyles: CopyableItemStyles): Promise<void> {
    const stylesObj: DeepPartial<QRCodeItemObject> = {};
    if (copiedStyles.frontFill !== undefined) {
      stylesObj.qrForegroundColor = convertFabricColorToFillObject(copiedStyles.frontFill).fillColor[0];
    }

    if (copiedStyles.backFill !== undefined) {
      stylesObj.isBackgroundTransparent = copiedStyles.backFill.fillType === FillTypes.NONE;
      stylesObj.qrBackgroundColor = copiedStyles.backFill.fillColor[0];
    }

    const commonStylesObj: DeepPartial<ItemObject> = this.createCommonStylesObject(copiedStyles, ['shadow', 'border', 'flipX', 'flipY', 'scaleX', 'scaleY']);
    await this.updateFromObject({...commonStylesObj, ...stylesObj});
  }

  public override async isPDFSafe(): Promise<boolean> {
    return true;
  }

  public override hasClickableLink(): boolean {
    return false;
  }

  public override toObject(): QRCodeItemObject {
    return {
      ...super.toObject(),
      message: this.message,
      qrType: this.qrType,
      qrForegroundColor: this.qrForegroundColor,
      qrBackgroundColor: this.qrBackgroundColor,
      isBackgroundTransparent: this.isBackgroundTransparent,
    };
  }

  public updateForegroundColor = (color: RGBA, undoable = true): void => {
    void this.updateFromObject(
      {
        qrForegroundColor: color,
      },
      {
        undoable,
      }
    );
  };


  public toggleBackgroundStateForQRItem = (): void => {
    void this.updateFromObject({
      isBackgroundTransparent: !this.isBackgroundTransparent,
    });
  };

  public updateQRBackgroundColor = (color: RGBA, undoable = true): void => {
    void this.updateFromObject(
      {
        qrBackgroundColor: color,
      },
      {
        undoable,
      }
    );
  };

  protected async getFabricObjectForItem(opts: InitItemOpts = {}): Promise<Group | FabricObject> {
    const qrCodeString = await QRCode.toString(this.message, {
      type: 'svg',
      width: 256,
      errorCorrectionLevel: 'medium',
      margin: 0,
      color: {
        light: this.isBackgroundTransparent ? '#FFFFFF01' : rgbaToHexString(this.qrBackgroundColor),
        dark: rgbaToHexString(this.qrForegroundColor),
      },
    });
    const svgParsed = await loadSVGFromString(qrCodeString);
    if (!svgParsed.objects) {
      throw new Error(`Failed to load svg for string ${this.message}`);
    }

    const objectsWithoutNulls = svgParsed.objects.filter((item) => {
      return item !== null;
    });
    const obj = util.groupSVGElements(objectsWithoutNulls, svgParsed.options);
    obj.set({
      ...this.getCommonOptions(),
      scaleX: opts?.scaleX ?? 1,
      scaleY: opts?.scaleY ?? 1,
    });
    return obj;
  }

  protected override onItemDoubleTapped(): void {
    getPosterUi().openPosterEditorQRContentModal();
  }
}

export const addQrCodeToPage = (message?: string, top?: number, left?: number): void => {
  const currentPage = window.posterEditor?.whiteboard?.getCurrentPage();
  if (!currentPage) {
    return;
  }

  const opts = {message: message  ?? window.posterEditor?.config?.businessProfileUrl ?? DEFAULT_QR_MESSAGE};
  void currentPage.items.addItems.addQRItem(
    {
      type: ElementDataType.QR_CODE,
      ...(top !== undefined && {top}),
      ...(left !== undefined && {left}),
    },
    opts
  );
};
